import enLocale from "@fullcalendar/core/locales/en-gb";
import plLocale from "@fullcalendar/core/locales/pl";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import multiMonthPlugin from "@fullcalendar/multimonth";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Box, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { MAX_WORK_TIME_IN_CALENDAR_STRING, MIN_WORK_TIME_STRING } from "../../helpers/constants";
import { EditTaskDialog } from "../../page/TaskPage/EditTaskDialog";
import { FinalizeTaskDialog } from "../../page/TaskPage/FinalizeTaskDialog";
import LocalstorageService from "../../services/localStorageService";
import { useUpdateTask, useGetTasksForUserWithDateRange } from "../../services/taskService";
import { EventTooltip, EventTooltipContent } from "./EventTooltip";
import LoaderWrapper from "../wrapper/LoaderWrapper";

export default function TaskCalendar({ sellerUserId, taskWay }) {
    const calendarRef = useRef(null);

    const { mutate: updateTask } = useUpdateTask();
    const [selectedTask, setSelectedTask] = useState(null);
    const [isFinalizeDialogOpen, setIsFinalizeDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    
    const { isLoading, data: tasks = [] } = useGetTasksForUserWithDateRange(
        sellerUserId,
        null,
        taskWay,
        dateRange.start,
        dateRange.end,
        {
            enabled: !!sellerUserId && !!dateRange.start && !!dateRange.end,
        }
    );
    
    const handleDatesSet = (dateInfo) => {
        const newStart = new Date(dateInfo.start);
        const newEnd = new Date(dateInfo.end);
        
        if (
            !dateRange.start || 
            !dateRange.end ||
            dateRange.start.getTime() !== newStart.getTime() ||
            dateRange.end.getTime() !== newEnd.getTime()
        ) {
            setDateRange({
                start: newStart,
                end: newEnd,
            });
        }
    };

    const handleDateClick = (arg) => {
        console.log(arg)
        if (arg.view.type === "dayGridMonth") {
            calendarRef.current?.get("timeGridDay", arg.dateStr);
        }
    };

    const handleEventClick = (arg) => {
        const task = arg.event.extendedProps.taskObject;
        setSelectedTask(task);
        setIsFinalizeDialogOpen(true);
    };

    const handleEditIconClick = (event, task) => {
        event.stopPropagation(); // Prevent the event click from firing
        setSelectedTask(task);
        setIsEditDialogOpen(true);
    };

    const handleCloseFinalizeDialog = () => {
        setIsFinalizeDialogOpen(false);
        setSelectedTask(null);
    };

    const handleCloseEditDialog = () => {
        setIsEditDialogOpen(false);
        setSelectedTask(null);
    };

    function moveEvent(event) {
        const payload = {
            start: event.event.start.toISOString(),
            stop: event.event.end?.toISOString() || null,
            is_allday: event.event.allDay,
        };
        return updateTask({
            task: event.event.extendedProps.taskObject,
            payload: payload,
        });
    }
    
    const filteredTasks = tasks.filter(
        (task) => task.status.status_name_eng !== "Finalized"
    );
    const events = convertTaskListToEvents(filteredTasks);

    return (
            <Box py={4} sx={{ width: "100%", height: "100vh", overflowY: "scroll" }}>
                {isLoading && <LoaderWrapper showLoader={true} />}
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin]}
                    initialView="timeGridWeek"
                    editable={true}
                    selectable={true}
                    locales={[plLocale, enLocale]}
                    locale={getLocale()}
                    events={events}
                    datesSet={handleDatesSet}
                    dateClick={handleDateClick}
                    eventClick={handleEventClick}
                    eventDrop={(arg) => {
                        calendarRef.current?.getApi().refetchEvents();
                        moveEvent(arg);
                    }}
                    eventResize={moveEvent}
                    eventMinHeight={30}
                    slotMinTime={MIN_WORK_TIME_STRING}
                    slotMaxTime={MAX_WORK_TIME_IN_CALENDAR_STRING}
                    slotMinWidth={100}
                    headerToolbar={{
                        start: "timeGridDay,timeGridWeek,dayGridMonth",
                        center: "title",
                        end: "prev,next today",
                    }}
                    height="auto"
                    views={{
                        timeGrid: {
                            eventContent: (eventInfo) =>
                                renderEventContent(eventInfo, handleEditIconClick),
                        },
                        dayGrid: {
                            eventContent: (eventInfo) => renderEventContentDayGrid(eventInfo, handleEditIconClick)
                                
                        },
                    }}
                />
                {selectedTask && isFinalizeDialogOpen && (
                    <FinalizeTaskDialog task={selectedTask} onClose={handleCloseFinalizeDialog} />
                )}
                {selectedTask && isEditDialogOpen && (
                    <EditTaskDialog task={selectedTask} onClose={handleCloseEditDialog} />
                )}
            </Box>
    );
}

function renderEventContent(event, onEditClick) {
    const task = event.event.extendedProps.taskObject;

    return (
        <EventTooltip
            title={<EventTooltipContent event={event} onEditClick={onEditClick} />}
            placement="top"
        >
            <Box
                className="fc-event-main-inner"
                sx={boldIfHighPriority(event)}
                display="flex"
                px={1}
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                overflow="hidden"
                height="100%"
                gap={1}
            >
                <Typography variant="subtitle1" fontWeight="inherit" noWrap flex={1}>
                    {event.event.title}
                </Typography>

                <Typography variant="caption" fontWeight="inherit" sx={{ flexShrink: 0 }}>
                    {event.timeText}
                </Typography>
            </Box>
        </EventTooltip>
    );
}

function renderEventContentDayGrid(event, onEditClick) {
    return (
        <EventTooltip
            title={<EventTooltipContent event={event} onEditClick={onEditClick} />}
            placement="top"
        >
            <Box
                className="fc-event-main-inner"
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                overflow="hidden"
                height="100%"
                gap={0.5}
            >
                <div className="fc-daygrid-event-dot" style={{borderColor: getBackgroundColorForTask(event.event.extendedProps)}} />
                {event.timeText}

                <div style={boldIfHighPriority(event)}>{event.event.title}</div>
            </Box>
        </EventTooltip>
    );
}

function boldIfHighPriority(event) {
    if (event.event.extendedProps.priority === "HIGH") {
        return { fontWeight: "bold" };
    }
    return {};
}

function convertTaskListToEvents(tasks) {
    return tasks.filter((task) => task.start !== null).map((task) => convertTaskToEvent(task));
}

function convertTaskToEvent(task) {
    const extendedProps = {
        way: task.way,
        done: task.done,
        note: task.note,
        priority: task.priority,
        subject: task.subject,
        subsubject: task.subsubject,
        customer: task.customer,
        seller: task.seller,
        products: task.products,
        contacts: task.contacts,
        status: task.status,
        taskObject: task,
    };

    return {
        id: task.id,
        title: task.name,
        start: task.start,
        end: task.stop,
        allDay: task.is_allday,
        backgroundColor: getBackgroundColorForTask(task),
        extendedProps: extendedProps,
    };
}

function getBackgroundColorForTask(task) {
    switch (task.way) {
        case "PHONE":
            return "#f5c71a";
        case "EMAIL":
            return "#483d8b";
        case "MEETING":
            return "#ab4b52";
    }
}

function getLocale() {
    const lang = LocalstorageService.getLanguage();
    return lang === "pl" ? plLocale : enLocale;
}
